/** @format */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { GlobeIcon } from 'src/components/icons';

type LanguageOption = {
  value?: string;
  title?: string;
  market?: string;
};

type ExtranetEditorLanguageDropdownProps = {
  languageOptions?: LanguageOption[];
  locale?: string;
  defaultLocale?: string;
  onLocaleChange?: (value: string) => void;
  placement?: 'bottom' | 'bottomRight' | 'bottomLeft' | 'top' | 'topRight' | 'topLeft';
  triggerId?: string;
};

export default function ExtranetEditorLanguageDropdown({
  languageOptions = [],
  locale,
  defaultLocale,
  onLocaleChange = () => {},
  placement = 'bottom',
  triggerId = 'extranet-editor-language-trigger'
}: ExtranetEditorLanguageDropdownProps) {
  const { t } = useTranslation();
  const [languageOpen, setLanguageOpen] = useState(false);

  const selectedLocaleLabel = useMemo(() => {
    const item = languageOptions.find(option => option.value === locale);
    return item ? item.title : t('common.selectLanguage');
  }, [languageOptions, locale, t]);

  return (
    <div className="extranet-editor-language">
      <div className="extranet-editor-language__field">
        <CustomDropDown
          className="extranet-editor-language__dropdown"
          value={locale}
          defaultValue={defaultLocale}
          onReset={value => onLocaleChange(String(value ?? defaultLocale))}
          resetAriaLabel={t('common.button.resetToDefault')}
          open={languageOpen}
          onOpenChange={setLanguageOpen}
          triggerOpensOnClick={false}
          closeOnPanelClick={false}
          placement={placement}
          trigger={
            <button
              id={triggerId}
              className="extranet-editor-language__trigger"
              type="button"
              aria-label={t('common.language')}
              aria-haspopup="menu"
              onClick={() => setLanguageOpen(prev => !prev)}>
              <span className="extranet-editor-language__trigger-label">{selectedLocaleLabel}</span>
              <GlobeIcon width={20} height={20} />
            </button>
          }>
          <ul className="dropdown-selection extranet-editor-language__menu" role="menu">
            {languageOptions.map(option => {
              const isActive = option.value === locale;
              return (
                <li key={option.value} role="none">
                  <button
                    className={`extranet-editor-language__menu-item${
                      isActive ? ' extranet-editor-language__menu-item--active' : ''
                    }`}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onLocaleChange(option.value);
                      setLanguageOpen(false);
                    }}>
                    {option.title}
                  </button>
                </li>
              );
            })}
          </ul>
        </CustomDropDown>
      </div>
    </div>
  );
}
